import type { ResponseError } from '@app/utils/types'

import { isResponseError } from '@app/utils/predicates'

function* exponentialBackoffGenerator(
  baseDelay: number,
  maxRetries: number,
  expotentialBase: number
): Generator<number> {
  let totalDelay = baseDelay
  yield totalDelay

  for (let retry = 1; retry < maxRetries; retry++) {
    totalDelay += Math.floor(Math.pow(expotentialBase, retry - 1))
    yield totalDelay
  }
}

const jitter = (maxNumber: number): number => {
  return Math.floor(Math.random() * maxNumber)
}

const PREDICATE_ERROR =
  'SFetch::WithRetry::Should retry the request based on the predicate'

export const MAX_RETRIES_ERROR = 'SFetch::WithRetry::Max retries exceeded'

function sleepUnlessAborted(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.resolve()
  return new Promise((resolve) => {
    // onAbort is declared as a function (hoisted) so the setTimeout callback
    // can reference it and so the `const timer` below can close over a fully
    // initialised binding without tripping prefer-const.
    function onAbort(): void {
      clearTimeout(timer)
      resolve()
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

// Callers with cancellation semantics pass `getSignal` so the backoff sleep
// and between-attempt check can short-circuit on abort. Throwing `signal.reason`
// re-uses the managed-abort marker (adapters/abort.ts) so the rejection is
// already shaped as an AbortError and the global unhandledrejection handler
// suppresses it without callers having to reconstruct the signal context.
function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return

  throw signal.reason ?? new DOMException('Aborted', 'AbortError')
}

/**
 * A function that retries a callback function with exponential backoff
 * @param base The base delay in milliseconds
 * @param callback The callback function to retry
 * @param expotentialBase The exponential base
 * @param maxRetries The maximum number of retries
 * @param shouldRetry A function that determines if the error should be retried
 * @param getSignal Optional resolver that extracts an AbortSignal from the
 *   call-time arguments. When the signal aborts during backoff, the wrapper
 *   throws `signal.reason` (a managed AbortError) so retries stop immediately.
 * @returns The return value of the callback function if it succeeds or the last error if it fails
 */
export function withRetry<P extends Array<unknown>, R>(
  callback: (...args: P) => Promise<R> | R,
  {
    base = 500,
    expotentialBase = 2,
    maxRetries = 2,
    shouldRetry,
    getSignal,
  }: {
    maxRetries?: number
    base?: number
    expotentialBase?: number
    shouldRetry?: (response: R | ResponseError) => boolean
    getSignal?: (args: P) => AbortSignal | undefined
  }
): (...args: P) => Promise<R> {
  return async function (...args: P) {
    const signal = getSignal?.(args)
    try {
      const response = await callback(...args)

      if (shouldRetry && shouldRetry(response)) {
        throw new Error(PREDICATE_ERROR)
      }

      return response
    } catch (e) {
      if (shouldRetry && isResponseError(e) && !shouldRetry(e)) {
        throw e
      }

      const backoffGenerator = exponentialBackoffGenerator(
        base / 1000,
        maxRetries,
        expotentialBase
      )

      for (const delay of backoffGenerator) {
        throwIfAborted(signal)
        try {
          const response = (await callback(...args)) as R

          if (shouldRetry && shouldRetry(response)) {
            throw new Error(PREDICATE_ERROR, { cause: e })
          }

          return response
        } catch {
          if (delay !== undefined) {
            // Randomised jitter avoids thundering-herd retry waves.
            await sleepUnlessAborted(delay * 1000 + jitter(2000), signal)
          }
        }
      }

      throwIfAborted(signal)
      throw new Error(MAX_RETRIES_ERROR, { cause: e })
    }
  }
}
